/**
 * Generic resource create/edit modal.
 *
 * Form alanlarını schema olarak alır; create/update mutation çağırır.
 *
 * Kullanım:
 *   <ResourceForm
 *     opened={opened}
 *     onClose={close}
 *     resource="suppliers"
 *     title="Yeni Tedarikçi"
 *     fields={[
 *       { name: "name", label: "Ad", required: true },
 *       { name: "phone", label: "Telefon" },
 *     ]}
 *   />
 *
 * Görsel yükleme: `type: "image"` alanı varsa payload otomatik olarak
 * `FormData` (multipart) gönderilir — Django ImageField doğrudan aynı
 * create/patch isteğiyle alır, ayrı upload endpoint'i gerekmez.
 */
import { Button, FileInput, Group, Image, Modal, MultiSelect, NumberInput, Select, Stack, Switch, Text, TextInput, Textarea } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconCalendar, IconPhoto, IconX } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useEffect, type ReactNode } from "react";
import { useCreate, useUpdate } from "../api/resources";
import { normalizePhoneTR, validateDate, validateEmail, validatePhoneTR } from "./validators";

export interface FormField {
  name: string;
  label: string;
  type?: "text" | "number" | "textarea" | "switch" | "email" | "phone" | "date" | "select" | "multiselect" | "money" | "image" | "custom";
  required?: boolean;
  placeholder?: string;
  defaultValue?: any;
  min?: number;
  options?: Array<{ value: string; label: string }>;
  clearable?: boolean;
  description?: string;
  /**
   * true ise alan yalnızca yeni kayıt oluştururken düzenlenebilir; düzenleme
   * modunda (editId varsa) salt-okunur gösterilir. Değeri otomatik/türetilmiş
   * alanlar için (örn. mal kabulden güncellenen "son maliyet").
   */
  readOnlyOnEdit?: boolean;
  /** readOnlyOnEdit aktifken düzenleme modunda gösterilecek ek açıklama. */
  readOnlyDescription?: string;
  /** Opsiyonel alan-bazlı doğrulama; null = geçerli, string = hata mesajı. */
  validate?: (value: any, values: Record<string, any>) => string | null;
  /**
   * type: "custom" alanlar için render callback'i. Alan değerini ve setter'ı
   * alır; tamamen özel bir widget (örn. server-side aranan picker) döndürür.
   * Düz Select/MultiSelect'in tüm seçenekleri DOM'a basması gereken büyük
   * listelerde (ürün, hammadde) dropdown patlamasını önlemek için kullanılır.
   */
  render?: (args: {
    value: any;
    onChange: (next: any) => void;
    values: Record<string, any>;
  }) => ReactNode;
}

/** "YYYY-MM-DD" / ISO string → DateInput'un beklediği Date | null. */
function toDateValue(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** DateInput'un Date | null değerini "YYYY-MM-DD" string'e (yerel saat) çevir. */
function toDateStr(v: Date | null): string {
  if (!v) return "";
  const y = v.getFullYear();
  const m = String(v.getMonth() + 1).padStart(2, "0");
  const d = String(v.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function minorToLira(v: any): any {
  if (v === null || v === undefined || v === "") return v;
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n / 100;
}

function liraToMinor(v: any): any {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/**
 * Alan tipine göre yerleşik format doğrulayıcı. email/phone/date alanları
 * her sayfada ayrı ayrı `validate` tanımlamaya gerek kalmadan otomatik
 * doğrulanır. Boş + opsiyonel değer hepsinde geçerli sayılır (validators.ts).
 */
function builtinValidator(type: FormField["type"]): ((v: any) => string | null) | null {
  switch (type) {
    case "email": return validateEmail;
    case "phone": return validatePhoneTR;
    case "date": return validateDate;
    default: return null;
  }
}

interface Props {
  opened: boolean;
  onClose: () => void;
  resource: string;
  title: string;
  fields: FormField[];
  initialValues?: Record<string, any>;
  editId?: string | null;
  transform?: (values: Record<string, any>) => Record<string, any>;
}

export function ResourceForm({
  opened, onClose, resource, title, fields,
  initialValues, editId, transform,
}: Props) {
  const create = useCreate(resource);
  const update = useUpdate(resource);

  const moneyFieldNames = fields.filter((f) => f.type === "money").map((f) => f.name);
  const imageFieldNames = fields.filter((f) => f.type === "image").map((f) => f.name);
  const phoneFieldNames = fields.filter((f) => f.type === "phone").map((f) => f.name);

  function applyMinorToLira(src: Record<string, any> | undefined): Record<string, any> | undefined {
    if (!src) return src;
    const out = { ...src };
    for (const n of moneyFieldNames) {
      if (n in out) out[n] = minorToLira(out[n]);
    }
    return out;
  }

  // Format alanları (telefon/e-posta/tarih) yazılırken anında doğrulansın —
  // submit'i beklemeden hata gösterilir. Diğer alanlar (zorunluluk vb.) eski
  // davranışta kalır (blur/submit), gereksiz erken "zorunlu" hataları çıkmasın.
  const liveValidateFields = fields
    .filter((f) => builtinValidator(f.type))
    .map((f) => f.name);

  const form = useForm({
    validateInputOnChange: liveValidateFields,
    initialValues: applyMinorToLira(initialValues) || fields.reduce((acc, f) => {
      acc[f.name] = f.defaultValue ?? (
        f.type === "switch" ? true
        : f.type === "number" ? 0
        : f.type === "money" ? null
        : f.type === "image" ? null
        : f.type === "multiselect" ? []
        : ""
      );
      if (f.type === "money" && f.defaultValue != null) {
        acc[f.name] = minorToLira(f.defaultValue);
      }
      return acc;
    }, {} as Record<string, any>),
    validate: fields.reduce((acc, f) => {
      const builtin = builtinValidator(f.type);
      if (f.required || f.validate || builtin) {
        acc[f.name] = (v: any, values: Record<string, any>) => {
          if (f.required && (v == null || v === "")) return `${f.label} zorunlu`;
          // Yerleşik format kontrolü (email/phone/date) — boş+opsiyonel geçer.
          if (builtin) {
            const err = builtin(v);
            if (err) return err;
          }
          if (f.validate) return f.validate(v, values);
          return null;
        };
      }
      return acc;
    }, {} as Record<string, any>),
  });

  // Sync initial values when modal reopens for editing
  useEffect(() => {
    if (opened && initialValues) {
      form.setValues(applyMinorToLira(initialValues) || {});
    } else if (opened && !initialValues) {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initialValues]);

  /**
   * Görsel alanı içeren formu multipart FormData'ya çevir.
   * - Yeni File seçildiyse → dosya eklenir.
   * - Değer string (mevcut URL) ise → değişmemiş, alan hiç gönderilmez
   *   (PATCH'te ImageField'i URL string ile ezmek hata verir).
   * - Değer null ise (kullanıcı kaldırdı) → boş string gönder (sil).
   */
  function toFormData(payload: Record<string, any>): FormData {
    const fd = new FormData();
    for (const [k, v] of Object.entries(payload)) {
      if (imageFieldNames.includes(k)) {
        if (v instanceof File) {
          fd.append(k, v);
        } else if (v === null || v === "") {
          fd.append(k, "");
        }
        // string URL → değişmemiş, atla
        continue;
      }
      if (v === null || v === undefined) continue;
      if (typeof v === "boolean") {
        fd.append(k, v ? "true" : "false");
      } else if (Array.isArray(v)) {
        v.forEach((item) => fd.append(k, String(item)));
      } else {
        fd.append(k, String(v));
      }
    }
    return fd;
  }

  async function handleSubmit(values: any) {
    const converted = { ...values };
    for (const n of moneyFieldNames) {
      if (n in converted) converted[n] = liraToMinor(converted[n]);
    }
    // Telefon alanlarını backend'e tek tip (+90XXXXXXXXXX) gönder.
    for (const n of phoneFieldNames) {
      if (n in converted && converted[n]) converted[n] = normalizePhoneTR(converted[n]);
    }
    const payload = transform ? transform(converted) : converted;

    // Görsel alanı varsa → multipart; yoksa eski JSON yolu.
    const hasNewFile = imageFieldNames.some((n) => payload[n] instanceof File);
    // Gerçek "görsel kaldırma" ancak kayıtta görsel VARKEN kullanıcı onu
    // null yaptıysa olur. Görseli hiç olmayan kayıtta değer zaten null'dır —
    // bu bir kaldırma değil, "değişmedi". O durumda image="" göndermek
    // backend'i "veri dosya değil" hatasına düşürür.
    const hasImageRemoval = !!editId && imageFieldNames.some(
      (n) => payload[n] === null && !!initialValues?.[n],
    );
    let body: any;
    if (hasNewFile || hasImageRemoval) {
      body = toFormData(payload);
    } else {
      // JSON yolu — görsel alanları gönderilmez. Değer ya değişmemiş URL
      // string'i ya da null'dır; ikisi de ImageField'e JSON ile yazılamaz
      // ("veri dosya değil"). Dosya yokken/silme yokken alanı çıkar.
      body = { ...payload };
      for (const n of imageFieldNames) delete body[n];
    }

    try {
      if (editId) {
        if (body instanceof FormData) {
          // id'yi FormData alanı olarak geç — useUpdate bunu okuyup URL'e
          // koyar ve göndermeden önce gövdeden çıkarır.
          body.append("id", editId);
          await update.mutateAsync(body);
        } else {
          await update.mutateAsync({ id: editId, ...body });
        }
        notifications.show({ message: "Güncellendi", color: "green" });
      } else {
        await create.mutateAsync(body);
        notifications.show({ message: "Oluşturuldu", color: "green" });
      }
      onClose();
      form.reset();
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || JSON.stringify(e?.response?.data || e),
        color: "red",
      });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {fields.map((f) => {
            const key = f.name;
            const fieldProps = form.getInputProps(key);
            // readOnlyOnEdit alanları düzenleme modunda kilitlenir; açıklama
            // o zaman readOnlyDescription'a (varsa) düşer.
            const isLocked = !!editId && !!f.readOnlyOnEdit;
            const fieldDescription = isLocked
              ? (f.readOnlyDescription ?? f.description)
              : f.description;
            switch (f.type) {
              case "number":
                return <NumberInput key={key} label={f.label} required={f.required} min={f.min} description={fieldDescription} readOnly={isLocked} variant={isLocked ? "filled" : undefined} {...(fieldProps as any)} />;
              case "money":
                return (
                  <NumberInput
                    key={key}
                    label={f.label}
                    required={f.required}
                    min={f.min}
                    description={fieldDescription}
                    placeholder={f.placeholder}
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator="."
                    decimalSeparator=","
                    suffix=" ₺"
                    readOnly={isLocked}
                    variant={isLocked ? "filled" : undefined}
                    {...(fieldProps as any)}
                  />
                );
              case "textarea":
                return <Textarea key={key} label={f.label} required={f.required} minRows={3} placeholder={f.placeholder} description={fieldDescription} readOnly={isLocked} variant={isLocked ? "filled" : undefined} {...(fieldProps as any)} />;
              case "switch":
                return <Switch key={key} label={f.label} {...(fieldProps as any)} checked={fieldProps.value} />;
              case "email":
                return <TextInput key={key} type="email" inputMode="email" label={f.label} required={f.required} placeholder={f.placeholder} description={fieldDescription} readOnly={isLocked} variant={isLocked ? "filled" : undefined} {...(fieldProps as any)} />;
              case "phone":
                return <TextInput key={key} type="tel" inputMode="tel" label={f.label} required={f.required} placeholder={f.placeholder || "+90 555 123 45 67"} description={fieldDescription} readOnly={isLocked} variant={isLocked ? "filled" : undefined} {...(fieldProps as any)} />;
              case "date":
                // Schema değeri "YYYY-MM-DD" string; DateInput (Mantine v7)
                // Date | null bekler. Sınırda iki yöne çeviririz; form state
                // string olarak kalır (backend ve initialValues uyumlu).
                return (
                  <DateInput
                    key={key}
                    label={f.label}
                    required={f.required}
                    placeholder={f.placeholder || "GG.AA.YYYY"}
                    description={fieldDescription}
                    valueFormat="DD.MM.YYYY"
                    leftSection={<IconCalendar size={16} />}
                    clearable={!f.required}
                    readOnly={isLocked}
                    variant={isLocked ? "filled" : undefined}
                    {...(fieldProps as any)}
                    value={toDateValue(fieldProps.value)}
                    onChange={(v: any) => form.setFieldValue(key, toDateStr(v as Date | null))}
                  />
                );
              case "custom":
                return (
                  <div key={key}>
                    {f.render?.({
                      value: fieldProps.value,
                      onChange: (next) => form.setFieldValue(key, next),
                      values: form.values,
                    })}
                  </div>
                );
              case "image": {
                const val = fieldProps.value;
                // Önizleme: yeni seçilen File ya da mevcut URL string'i
                const previewUrl =
                  val instanceof File ? URL.createObjectURL(val)
                  : typeof val === "string" && val ? val
                  : null;
                return (
                  <Stack key={key} gap={6}>
                    <FileInput
                      label={f.label}
                      description={f.description || "JPEG / PNG / WebP"}
                      placeholder={f.placeholder || "Görsel seç"}
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      leftSection={<IconPhoto size={16} />}
                      clearable
                      value={val instanceof File ? val : null}
                      onChange={(file) => form.setFieldValue(key, file)}
                    />
                    {previewUrl && (
                      <Group gap="xs" align="center">
                        <Image
                          src={previewUrl}
                          h={64}
                          w={64}
                          radius="sm"
                          fit="cover"
                          alt={f.label}
                        />
                        <Button
                          variant="subtle"
                          color="red"
                          size="compact-xs"
                          leftSection={<IconX size={12} />}
                          onClick={() => form.setFieldValue(key, null)}
                        >
                          Görseli kaldır
                        </Button>
                      </Group>
                    )}
                    {!previewUrl && (
                      <Text size="xs" c="dimmed">Henüz görsel yok</Text>
                    )}
                  </Stack>
                );
              }
              case "select":
                return (
                  <Select
                    key={key}
                    label={f.label}
                    required={f.required}
                    placeholder={f.placeholder}
                    description={f.description}
                    data={f.options || []}
                    clearable={f.clearable}
                    searchable
                    {...(fieldProps as any)}
                  />
                );
              case "multiselect":
                return (
                  <MultiSelect
                    key={key}
                    label={f.label}
                    required={f.required}
                    placeholder={f.placeholder}
                    description={f.description}
                    data={f.options || []}
                    searchable
                    clearable={f.clearable}
                    {...(fieldProps as any)}
                  />
                );
              default:
                return <TextInput key={key} label={f.label} required={f.required} placeholder={f.placeholder} description={fieldDescription} readOnly={isLocked} variant={isLocked ? "filled" : undefined} {...(fieldProps as any)} />;
            }
          })}
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>İptal</Button>
            <Button type="submit" loading={create.isPending || update.isPending}>
              {editId ? "Güncelle" : "Oluştur"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
